'use client'

import { formatPrice, orderFlowPriceFormatOptions } from '@/_lib/utils'
import { useReportProductLinePrice } from '@cart/_hooks/use-report-product-line-price'


import { type CartVariant, ModuleRow, ProductNameRow } from './a-cart-ui-primitives'

import type { PriceInfo } from '@cart/_hooks/use-cart-prices'

type Module = { id: number; name: string; price: number }

type ProductSectionProps = {
	name: string
	namePrice: number | null
	savings?: number
	familySavings?: number
	/** When false, per-line price hides inline discount; family 10% stays in summary only. @default true */
	showDiscountInline?: boolean
	discountLabel?: string
	discountLabelTitle?: string
	modules?: Module[]
	onPriceReport?: (info: PriceInfo) => void
	variant: CartVariant
	/**
	 * Label shown to the left of the price in the sidebar row.
	 * Defaults to "Cena" (used for self-payer and when no insurer is selected).
	 * Set to "Manažment návštevy" for insured patients once an insurer is chosen.
	 */
	priceLabel?: string
	/**
	 * When true, "od " is prepended before the formatted price in both sidebar and compact views.
	 * Indicates a minimum "starting from" price before the patient selects an insurer.
	 */
	showFromPrefix?: boolean
}

/**
 * Product title, optional price row, optional modules, and sidebar vs compact layout.
 * Emits `onPriceReport` when computed totals change without depending on callback identity.
 */
export function ProductSection({
	name,
	namePrice,
	savings = 0,
	familySavings = 0,
	showDiscountInline = true,
	discountLabel,
	discountLabelTitle,
	modules,
	onPriceReport,
	variant,
	priceLabel = 'Cena',
	showFromPrefix = false
}: ProductSectionProps) {
	const mods = modules ?? []
	const moduleTotal = mods.reduce((sum, m) => sum + m.price, 0)
	const totalSavings = savings + familySavings
	const total = (namePrice ?? 0) - totalSavings + moduleTotal

	useReportProductLinePrice(onPriceReport, {
		total,
		savings: totalSavings,
		namePrice,
		name
	})

	const basePrice = showDiscountInline ? (namePrice ?? 0) - totalSavings : (namePrice ?? 0)
	const baseOriginal = showDiscountInline && totalSavings > 0 ? (namePrice ?? 0) : undefined
	const originalTotal = totalSavings > 0 ? (namePrice ?? 0) + moduleTotal : undefined

	// "od " prefix for the "from" price (before insurer selection).
	const prefix = showFromPrefix ? 'od\u00a0' : undefined

	if (variant === 'compact') {
		return (
			<div className='space-y-0.5'>
				<ProductNameRow
					name={name}
					price={total}
					oldPrice={originalTotal}
					pricePrefix={prefix}
					variant='compact'
				/>
				{discountLabel && (
					<p className='text-xs font-medium text-medante-purple-dark/60'>
						{discountLabel}
					</p>
				)}
				{mods.length > 0 && (
					<p className='text-xs text-medante-purple-dark/80'>
						Cena vrátane {mods.length} {mods.length === 1 ? 'modulu' : 'modulov'}
					</p>
				)}
			</div>
		)
	}

	const fBase = formatPrice(basePrice, orderFlowPriceFormatOptions)
	const fBaseOriginal =
		baseOriginal != null ? formatPrice(baseOriginal, orderFlowPriceFormatOptions) : null

	return (
		<div className='space-y-1.5'>
			<p className='font-semibold leading-snug text-medante-purple-dark md:text-lg'>{name}</p>

			{basePrice > 0 && (
				<div className='flex items-baseline justify-between gap-4'>
					<p className='text-sm font-medium text-medante-purple-dark/80'>{priceLabel}</p>
					<div className='flex items-baseline gap-2'>
						<p className='font-medium text-medante-purple-dark'>
							{prefix}
							{fBase.price}&nbsp;{fBase.currency}
						</p>
						{fBaseOriginal && (
							<p className='text-sm text-medante-purple-dark/40 line-through'>
								{fBaseOriginal.price}&nbsp;{fBaseOriginal.currency}
							</p>
						)}
					</div>
				</div>
			)}

			{discountLabel &&
				(discountLabelTitle ? (
					<div className='flex items-baseline justify-between gap-4'>
						<p className='text-sm font-medium text-medante-purple-dark/80'>
							{discountLabelTitle}
						</p>
						<p className='text-right text-sm font-medium text-medante-purple-dark'>
							{discountLabel}
						</p>
					</div>
				) : (
					<p className='text-sm font-normal text-medante-purple-dark/80'>
						{discountLabel}
					</p>
				))}

			{mods.length > 0 && (
				<ul className='space-y-1 list-none pl-0 mt-1'>
					{mods.map(m => (
						<ModuleRow key={m.id} name={m.name} price={m.price} variant='sidebar' />
					))}
				</ul>
			)}
		</div>
	)
}
